import { Code, ConnectError } from "@connectrpc/connect";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { usePageView } from "@viajero/shared-ui/analytics";
import { Carousel } from "@/components/Carousel";
import { QueryStateFallback } from "@/components/QueryStateFallback";
import { useCheckAvailability } from "@/features/shops/api/useCheckAvailability";
import { useGetReservationContext } from "@/features/shops/api/useGetReservationContext";
import { useGetShopDetail } from "@/features/shops/api/useGetShopDetail";
import { ReservationSection } from "@/features/shops/components/ReservationSection";
import { buildAvailabilityVisit } from "@/features/shops/lib/availabilityVisit";
import { redactPath } from "@/lib/analytics/redactPath";
import { ShopInfoSection } from "@/features/shops/components/ShopInfoSection";
import { ShopMenuList } from "@/features/shops/components/ShopMenuList";
import { isValidId } from "@/lib/id";
import { colors } from "@/theme/colors";

type Props = {
  caseToken: string;
  shopId: string;
};

export function ShopDetailView({ caseToken, shopId }: Props) {
  const { t } = useTranslation();
  const query = useGetShopDetail(shopId, Boolean(caseToken));
  const reservationContextQuery = useGetReservationContext(
    caseToken,
    Boolean(caseToken) && isValidId(shopId),
  );
  // 予約希望日時・人数が揃った場合のみ空席確認する (揃わない・確認エラー時はチップ非表示)。
  const availability = useCheckAvailability(
    shopId,
    buildAvailabilityVisit(
      reservationContextQuery.data?.preferredDate ?? "",
      reservationContextQuery.data?.preferredTime ?? "",
      reservationContextQuery.data?.preferredPartySize ?? "",
    ),
  );
  // 店舗詳細の page_view は店舗名を載せるため、router 直下の PageViewTracker ではなくここで送る
  // (JPTP_CANSEI-1400)。店舗名は RPC の応答に含まれるため、取得完了まで待ってから 1 回だけ送る。
  usePageView(redactPath, {
    enabled: Boolean(query.data?.shop),
    params: { shop_name: query.data?.shop?.name },
  });
  // URL の shopId が不正 (非数値) な場合、useGetShopDetail はクエリを無効化し
  // リクエスト自体を行わない。この場合も「見つからない」として扱う (NotFound と同様の表示)。
  const isNotFound =
    !isValidId(shopId) ||
    (query.error instanceof ConnectError && query.error.code === Code.NotFound);

  if (query.isLoading || isNotFound || query.isError || !query.data?.shop) {
    return (
      <QueryStateFallback
        isLoading={query.isLoading}
        isNotFound={isNotFound}
        isError={query.isError || !query.data?.shop}
        notFound={{
          title: t("shopDetail.notFound"),
          description: t("shopDetail.notFoundDescription"),
        }}
        error={{
          title: t("shopDetail.error"),
          description: t("shopDetail.errorDescription"),
          actions: [{ label: t("errorPage.reload"), onClick: () => void query.refetch() }],
        }}
      />
    );
  }

  const { shop, products, note } = query.data;

  return (
    <Box sx={{ pb: { xs: 4, sm: 6 }, maxWidth: 640, width: "100%", mx: "auto" }}>
      <Carousel images={shop.imageUrl}>
        {shop.michelinStar > 0 && (
          <Chip
            label={`${"★".repeat(shop.michelinStar)}`}
            size="small"
            sx={{ bgcolor: colors.michelin, color: "#fff", fontWeight: 700, fontSize: 12 }}
          />
        )}
      </Carousel>

      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 2, sm: 3 } }}>
        <Typography
          component="h1"
          variant="h5"
          sx={{ fontWeight: 700, color: colors.text, mb: 2, px: 1.5 }}
        >
          {shop.name}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {shop.description && (
          <Typography
            sx={{ color: colors.textBody, fontSize: 16, whiteSpace: "pre-line", px: 1.5 }}
          >
            {shop.description}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ px: 1.5 }}>
          <ShopInfoSection
            address={shop.address}
            access={shop.access}
            phoneNumber={shop.phoneNumber}
            businessHour={shop.businessHour}
            regularClosingDay={shop.regularClosingDay}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ px: 1.5 }}>
          <ReservationSection
            loading={reservationContextQuery.isLoading}
            preferredDate={reservationContextQuery.data?.preferredDate ?? ""}
            preferredTime={reservationContextQuery.data?.preferredTime ?? ""}
            availability={availability}
          />
        </Box>

        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: colors.text, mt: 3, mb: 1.5, px: 1.5 }}
        >
          {t("shopDetail.menuTitle")}
        </Typography>
        <ShopMenuList caseToken={caseToken} shopId={shopId} products={products} />

        {note && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text, mb: 1, px: 1.5 }}>
              {t("shopDetail.notes")}
            </Typography>
            <Typography
              sx={{ color: colors.textBody, fontSize: 13, whiteSpace: "pre-line", px: 1.5 }}
            >
              {note}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
